/**
 * Thin fetch wrapper for the iHatod API.
 *  - Sends the bearer token, parses the {data, meta} / {error} envelopes
 *  - Throws ApiError with per-field messages for validation failures
 *  - Signs the user out automatically when the server says the session is gone
 */

const BASE = '/api/v1';
const TOKEN_KEY = 'ihatod-token';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ---------------------------------------------------------------- token storage */

type Stored = { token: string; remember: boolean };

export const tokenStore = {
  get(): string | null {
    for (const s of [sessionStorage, localStorage]) {
      try {
        const raw = s.getItem(TOKEN_KEY);
        if (raw) return (JSON.parse(raw) as Stored).token;
      } catch {
        /* storage blocked or corrupt */
      }
    }
    return null;
  },
  set(token: string, remember: boolean) {
    this.clear();
    try {
      (remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, JSON.stringify({ token, remember } satisfies Stored));
    } catch {
      /* private mode: the session simply won't survive a reload */
    }
  },
  clear() {
    for (const s of [sessionStorage, localStorage]) {
      try {
        s.removeItem(TOKEN_KEY);
      } catch {
        /* ignore */
      }
    }
  },
};

export const UNAUTHORIZED_EVENT = 'ihatod:unauthorized';

/* ---------------------------------------------------------------- request core */

type Query = Record<string, string | number | boolean | undefined | null>;

interface Options {
  query?: Query;
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  /** do not treat a 401 as "session expired" (e.g. wrong password on the login form) */
  anonymous?: boolean;
  /** sent as Idempotency-Key, so a retry of the same action is answered with the first response (see useIdempotencyKey) */
  idempotencyKey?: string;
}

export interface Envelope<T> {
  data: T;
  meta?: Record<string, any>;
}

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function raw(method: string, path: string, o: Options = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (o.idempotencyKey) headers['Idempotency-Key'] = o.idempotencyKey;
  let body: BodyInit | undefined;
  if (o.form) {
    body = o.form; // the browser sets the multipart boundary
  } else if (o.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(o.body);
  }

  let res: Response;
  try {
    res = await fetch(BASE + path + qs(o.query), { method, headers, body, signal: o.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('Cannot reach the server. Check your connection and try again.', 0, 'network_error');
  }

  if (!res.ok) {
    let err: { code?: string; message?: string; fields?: Record<string, string> } = {};
    try {
      err = (await res.json()).error ?? {};
    } catch {
      /* non-JSON error page */
    }
    if (res.status === 401 && token && !o.anonymous) {
      tokenStore.clear();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(err.message ?? `Request failed (${res.status}).`, res.status, err.code ?? 'error', err.fields ?? {});
  }
  return res;
}

async function json<T>(method: string, path: string, o?: Options): Promise<Envelope<T>> {
  const res = await raw(method, path, o);
  if (res.status === 204) return { data: undefined as T };
  return (await res.json()) as Envelope<T>;
}

export const api = {
  get: async <T>(path: string, query?: Query, signal?: AbortSignal) => (await json<T>('GET', path, { query, signal })).data,
  /** For list endpoints: returns data plus pagination meta. */
  page: async <T>(path: string, query?: Query, signal?: AbortSignal): Promise<{ data: T[]; meta: any }> => {
    const e = await json<T[]>('GET', path, { query, signal });
    return { data: e.data, meta: e.meta };
  },
  post: async <T>(path: string, body?: unknown, o?: Pick<Options, 'anonymous' | 'query' | 'idempotencyKey'>) => (await json<T>('POST', path, { body: body ?? {}, ...o })).data,
  put: async <T>(path: string, body?: unknown) => (await json<T>('PUT', path, { body: body ?? {} })).data,
  del: async <T = void>(path: string) => (await json<T>('DELETE', path)).data,
  /** multipart/form-data (files + fields) */
  upload: async <T>(path: string, form: FormData, o?: Pick<Options, 'anonymous' | 'idempotencyKey'>) => (await json<T>('POST', path, { form, ...o })).data,
  /** Authenticated file download (documents are never directly addressable). */
  blob: async (path: string): Promise<Blob> => (await raw('GET', path)).blob(),
  /** Customer tracking uses a secret link token instead of a login. */
  track: {
    get: async <T>(code: string, auth: TrackAuth) => (await json<T>('GET', `/public/track/${encodeURIComponent(code)}`, { query: trackQuery(auth), anonymous: true })).data,
    post: async <T>(code: string, action: string, auth: TrackAuth, body: unknown, idempotencyKey?: string) =>
      (await json<T>('POST', `/public/track/${encodeURIComponent(code)}/${action}`, { query: trackQuery(auth), body, anonymous: true, idempotencyKey })).data,
    upload: async <T>(code: string, action: string, auth: TrackAuth, form: FormData) =>
      (await json<T>('POST', `/public/track/${encodeURIComponent(code)}/${action}`, { query: trackQuery(auth), form, anonymous: true })).data,
    blob: async (code: string, docId: number, auth: TrackAuth): Promise<Blob> =>
      (await raw('GET', `/public/track/${encodeURIComponent(code)}/documents/${docId}`, { query: trackQuery(auth), anonymous: true })).blob(),
  },
};

export type TrackAuth = { t?: string; p?: string };
const trackQuery = (a: TrackAuth): Query => ({ t: a.t, p: a.p });

/** A random Idempotency-Key. crypto.randomUUID only exists on https/localhost, so plain-http LAN testing falls back. */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Turn any thrown value into a message fit for a toast. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong. Please try again.';
}
